/**
 * Voice Session Token Service
 *
 * Provides authentication tokens for voice session providers.
 * Abstracts demo mode (env var) vs production (ephemeral tokens).
 *
 * SECURITY:
 * - Demo mode: Uses VITE_GEMINI_API_KEY from env (dev only)
 * - Production mode: Fetches ephemeral token from backend
 *
 * Demo mode logs warnings and should NEVER be used in production.
 *
 * @module gemini/token-service
 */

// ============================================================================
// Token Configuration
// ============================================================================

export interface TokenServiceConfig {
  /**
   * Mode of operation
   * - 'demo': Use env var API key (dev only, logs warnings)
   * - 'production': Fetch ephemeral token from backend
   * - 'proxy': Use authToken directly (backend proxy handles Vertex AI auth)
   */
  mode: 'demo' | 'production' | 'proxy';

  /**
   * Backend endpoint for ephemeral token generation (production mode)
   * Should return: { token: string, expiresAt: number }
   */
  tokenEndpoint?: string;

  /**
   * Assessment ID for scoping the token (production mode)
   */
  assessmentId?: string;

  /**
   * Authorization token for tenant isolation (production/proxy mode)
   * In proxy mode, this token is passed directly to the backend WebSocket proxy
   */
  authToken?: string;
}

export interface TokenResult {
  token: string;
  expiresAt: number | null; // null for demo/proxy mode (no expiry tracking)
  mode: 'demo' | 'production' | 'proxy';
}

// ============================================================================
// Token Service
// ============================================================================

/**
 * Get a token for voice session authentication
 *
 * @param config - Token service configuration
 * @returns Token result with mode information
 * @throws Error if no token available
 */
export async function getVoiceSessionToken(
  config: TokenServiceConfig
): Promise<TokenResult> {
  if (config.mode === 'demo') {
    return getDemoToken();
  } else if (config.mode === 'proxy') {
    return getProxyToken(config);
  } else {
    return getProductionToken(config);
  }
}

/**
 * Proxy mode: Use authToken directly
 *
 * Backend proxy handles Vertex AI authentication using ADC.
 * The learner JWT is used to authenticate with the proxy.
 */
function getProxyToken(config: TokenServiceConfig): TokenResult {
  if (!config.authToken) {
    throw new Error(
      'Proxy mode requires authToken. ' +
        'Pass the learner JWT from the URL token parameter.'
    );
  }

  return {
    token: config.authToken,
    expiresAt: null, // JWT has its own expiry, proxy will validate
    mode: 'proxy',
  };
}

/**
 * Demo mode: Get API key from environment
 *
 * SECURITY: Logs prominent warnings. Dev only.
 */
function getDemoToken(): TokenResult {
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY as string | undefined;

  if (!apiKey) {
    throw new Error(
      'Voice session requires VITE_GEMINI_API_KEY in .env file. ' +
        'See .env.example for setup instructions.'
    );
  }

  // Prominent warning for demo mode
  console.warn(
    '%c⚠️ DEMO MODE: Using API key from environment variable',
    'background: #ff6b6b; color: white; padding: 4px 8px; font-weight: bold;'
  );
  console.warn(
    'This is for local development only. ' +
      'Production deployments MUST use ephemeral tokens from the backend.'
  );

  // Check if key looks like a long-lived API key (starts with AIza)
  if (apiKey.startsWith('AIza')) {
    console.warn(
      '%c🔴 SECURITY RISK: Long-lived API key detected',
      'background: #c0392b; color: white; padding: 4px 8px; font-weight: bold;'
    );
    console.warn(
      'Long-lived API keys in browser code are a security risk. ' +
        'Migrate to ephemeral tokens before production deployment.'
    );
  }

  return {
    token: apiKey,
    expiresAt: null,
    mode: 'demo',
  };
}

/**
 * Production mode: Fetch ephemeral token from backend
 *
 * Backend should generate short-lived (10 min) Gemini session token.
 */
async function getProductionToken(
  config: TokenServiceConfig
): Promise<TokenResult> {
  if (!config.tokenEndpoint) {
    throw new Error(
      'Production mode requires tokenEndpoint in config. ' +
        'Configure backend endpoint for ephemeral token generation.'
    );
  }

  if (!config.assessmentId) {
    throw new Error(
      'Production mode requires assessmentId for token scoping.'
    );
  }

  if (!config.authToken) {
    throw new Error(
      'Production mode requires authToken for tenant isolation. ' +
        'Pass the learner JWT from the URL token parameter.'
    );
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${config.authToken}`,
  };

  const response = await fetch(config.tokenEndpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      assessmentId: config.assessmentId,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch voice session token: ${response.status} ${response.statusText}`
    );
  }

  const data = (await response.json()) as { token: string; expiresAt: number };

  if (!data.token) {
    throw new Error('Backend returned invalid token response');
  }

  return {
    token: data.token,
    expiresAt: data.expiresAt,
    mode: 'production',
  };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Check if running in demo mode (based on env var presence)
 */
export function isDemoMode(): boolean {
  return !!import.meta.env.VITE_GEMINI_API_KEY;
}

/**
 * Check if proxy mode is enabled
 * Proxy mode: Backend handles Vertex AI auth, frontend just passes learner JWT
 */
export function isProxyMode(): boolean {
  return import.meta.env.VITE_VOICE_PROXY_ENABLED === 'true';
}

/**
 * Check if token endpoint is configured (production ready)
 */
export function isProductionReady(): boolean {
  // Check for backend token endpoint env var
  return !!import.meta.env.VITE_VOICE_TOKEN_ENDPOINT;
}

/**
 * Get recommended mode based on environment
 */
export function getRecommendedMode(): 'demo' | 'production' | 'proxy' {
  // Proxy mode takes precedence - backend handles all Vertex AI auth
  if (isProxyMode()) {
    return 'proxy';
  }
  if (isProductionReady()) {
    return 'production';
  }
  if (isDemoMode()) {
    return 'demo';
  }
  throw new Error(
    'No voice authentication configured. ' +
      'Set VITE_VOICE_PROXY_ENABLED=true for proxy mode, ' +
      'VITE_GEMINI_API_KEY for demo, or VITE_VOICE_TOKEN_ENDPOINT for production.'
  );
}
